import React, { useEffect, useState, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import { useLocation, useNavigate } from "react-router-dom";
import Stack from "@mui/material/Stack";
import LevelSelectionCarousel from "./LevelSelectionCarousel";
import OpponentSelectionCarousel from "./OpponentSelectionCarousel";
import useOpponentsList from "../entities/useOpponentsList.js";
import useLevelsList from "../entities/useLevelsList.js";
import MovingText from "react-moving-text";
import { sendMessage } from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import { LottieAnimation } from "../lottie/LottieAnimation.js";

import GameInfoDialog from "./GameInfoDialog";
import { useWebSocket } from "../services/WebSocketProvider";
import NotEnoughCoinsDialog from "./NotEnoughCoinsDialog";
import ChessButton from "./ChessButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { GENERAL } from "../general/General";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import FortIcon from "@mui/icons-material/Fort";
import CancelIcon from "@mui/icons-material/Cancel";
import { Divider, Paper } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Swal from "sweetalert2";
import Profile from "./Profile.js";
const TeamPlay = ({ userData, updateBackground, setShowAppBar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { generalMessage } = state || {};
  const { t } = useTranslation();
  const opponents = useOpponentsList();
  const levels = useLevelsList();
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMobile] = useState(window.innerWidth <= 600);
  const [openDialog, setOpenDialog] = useState(false);
  const [uncompletedGame, setUncompletedGame] = useState(null);
  const { uncompletedGameWS } = useWebSocket();
  const [gameInfoDialogOpen, setGameInfoDialogOpen] = useState(false);
  const [showOpponentSelection, setShowOpponentSelection] = useState(false);
  const [showLevelSelection, setShowLevelSelection] = useState(false);
  const [buttonSelectOpponent, setButtonSelectOpponent] = useState(
    t("Select_opponent")
  );
  const [buttonSelectLevel, setButtonSelectLevel] = useState(t("Level_Select"));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [neededCoins, setNeededCoins] = useState(null);
  const [actualCoins, setActualCoins] = useState(null);

  const { team } = location.state || {};

  // Utility function for setting button labels from local storage
  const setButtonLabelsFromLocalStorage = useCallback(() => {
    const levelIndex = localStorage.getItem("lastSelectedIndexLevel");
    const opponentIndex = localStorage.getItem("lastSelectedOpponentIndex");
    const timeLimit = localStorage.getItem("lastTimeLimit");

    if (levelIndex && levels && levels.length > 0) {
      setButtonSelectLevel(levels[levelIndex].name);
    }

    if (opponentIndex && opponents && opponents.length > 0) {
      setButtonSelectOpponent(opponents[opponentIndex]?.name);
    }

    // Set time limit text
    if (timeLimit === "1") {
      setTimeLimitText(t("MINUTES_PER_MOVE_1"));
    } else if (timeLimit === "30") {
      setTimeLimitText(t("MINUTES_PER_GAME_30"));
    } 
    else if (timeLimit === "3") {
      setTimeLimitText(t("MINUTES_PER_GAME_3"));
    } 
    else if (timeLimit === "5") {
      setTimeLimitText(t("МINUTES_PER_GAME_5"));
    } 
    else {
      setTimeLimitText(t("NO_LIMIT"));
    }
  }, [levels, opponents, t]);

  // Utility function for setting time limit text
  const setTimeLimitText = (text) => {
    // You can add state here if you need to use this text elsewhere
    // Or just use it directly in the logic that needs it.
  };

  const renderTime = ({ remainingTime }) => {
    if (remainingTime === 0) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 96,
            width: 96,
            color: "#e53935",
            fontWeight: 600,
            fontSize: 16,
            fontFamily: "sans-serif",
            textAlign: "center",
          }}
        >
          {t("No opponent found")}
        </div>
      );
    }

    return (
      <div
        style={{
          position: "relative",
          width: 96,
          height: 96,
          borderRadius: "50%",
          backgroundColor: "#4a707d",
          color: "white",
          boxShadow: "0 4px 10px rgba(66, 48, 48, 0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "sans-serif",
        }}
      >
        {/* Top Label */}
        <div
          style={{
            position: "absolute",
            top: 6,
            width: "100%",
            textAlign: "center",
            fontSize: 10,
            color: "white",
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          {t("Time Left")}
        </div>

        {/* Centered Value */}
        <div
          style={{
            marginTop: "15px",
            fontSize: 28,
            fontWeight: "bold",
            lineHeight: 1,
          }}
        >
          {remainingTime}
        </div>

        {/* Bottom Label */}
        <div
          style={{
            position: "absolute",
            bottom: 6,
            width: "100%",
            textAlign: "center",
            fontSize: 10,
            color: "white",
          }}
        >
          {t("seconds")}
        </div>
      </div>
    );
  };

  useEffect(() => {
    setButtonLabelsFromLocalStorage();
    const uncompletedGameFromStorage = localStorage.getItem("uncompleted_game");
    if (uncompletedGameFromStorage) {
      setUncompletedGame(JSON.parse(uncompletedGameFromStorage));
    }
  }, [setButtonLabelsFromLocalStorage]);

  useEffect(() => {
    if (uncompletedGameWS) {
      setUncompletedGame(uncompletedGameWS);
    }
  }, [uncompletedGameWS]);

  useEffect(() => {
    setLoading(false);
    const handlePopState = () => {};
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);

  useEffect(() => {
    document.body.style.overflow = loading
      ? "hidden"
      : isMobile
        ? "auto"
        : "hidden";
    updateBackground({ image: GENERAL.BACKGROUND });
    return () => (document.body.style.overflow = "auto");
  }, [isMobile, loading, updateBackground]);

  useEffect(() => {
    if (generalMessage) {
      playGame();
    }
  }, [generalMessage]);

  const cancelGame = () => {
    setLoading(false);
    const generalMessage = {
      ...generalMessageTemplate,
      messageType: "new_game_request_cancel",
    };
    sendMessage(generalMessage);
    setShowAppBar(true);
  };

  const handleAvatarClick = (user) => {
    setSelectedAvatar(user);
    setProfileOpen(true);
  };

  const handleCloseProfile = () => {
    setSelectedAvatar(null);
    setProfileOpen(false);
  };

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);
  const handleBuyCoins = () => setIsDialogOpen(false);
  const handleWatchAd = () => setIsDialogOpen(false);

  const findLevelByTypeId = (levelTypeId) => {
    const storedLevels = JSON.parse(localStorage.getItem("levels"));
    return (
      storedLevels?.find((level) => level.levelTypeId === +levelTypeId) || null
    );
  };

  function createMinimalTeamCopy(team) {
    return {
      id: team.id ?? null,
      name: team.name ?? null,
      description: team.description ?? null,
      userId: team.userId ?? null,
      whiteTeam: team.whiteTeam.map((player) => ({
        id: player.id ?? null,
        userName: player.userName ?? null, // or whatever minimal fields you need
      })),
      blackTeam: team.blackTeam.map((player) => ({
        id: player.id ?? null,
        userName: player.userName ?? null, // same
      })),
    };
  }

  const playGame = () => {
    const levelId = localStorage.getItem("lastSelectedLevelId");
    const opponentIndex = localStorage.getItem("lastSelectedOpponentIndex");
    const storedOpponents = JSON.parse(localStorage.getItem("opponents"));

    const levelsObj = findLevelByTypeId(levelId);
    const opponentObj = storedOpponents?.[+opponentIndex];

    if (opponentObj?.gameType === "skill_building") {
      navigate("/learn", { state: { userData } });
      return;
    }

    if (!levelId || !opponentIndex) {
      setOpenDialog(true);
      return;
    }

    let res = checkBeforePlay();
    if (!res) {
      return;
    }

    if (
      userData &&
      (Math.abs(levelsObj?.loseCoins) > userData.userStatistics.coins ||
        Math.abs(levelsObj?.drawCoins) > userData.userStatistics.coins)
    ) {
      setNeededCoins(
        Math.abs(levelsObj?.loseCoins) > Math.abs(levelsObj.drawCoins)
          ? Math.abs(levelsObj?.loseCoins)
          : Math.abs(levelsObj?.drawCoins)
      );
      setActualCoins(userData.userStatistics.coins);

      setTimeout(() => {
        handleOpenDialog();
      }, 100); // delay slightly to let the button animation finish
      return;
    }

    //setLoading(true);

    //setShowAppBar(false);

    const generalMessageToSend = { ...generalMessageTemplate };
    generalMessageToSend.messageType = "invite_to_team_game";
    generalMessageToSend.gameInfo.gameOpponentId = opponentIndex;
    generalMessageToSend.gameInfo.gameLevelId = levelId;
    const minimalTeam = createMinimalTeamCopy(team);

    // assign it to your message
    generalMessageToSend.team = minimalTeam;

    generalMessageToSend.gameInfo.pointsWin = levelsObj?.win;
    generalMessageToSend.gameInfo.pointsLose = levelsObj?.lose;
    generalMessageToSend.gameInfo.pointsDraw = levelsObj?.draw;

    generalMessageToSend.gameInfo.coinsWin = levelsObj?.winCoins;
    generalMessageToSend.gameInfo.coinsDraw = levelsObj?.drawCoins;
    generalMessageToSend.gameInfo.coinsLose = levelsObj?.loseCoins;

    generalMessageToSend.gameInfo.gameTeamType = opponentObj?.gameType;
    generalMessageToSend.gameInfo.userType = "Human";
    generalMessageToSend.gameInfo.opponentType = localStorage.getItem(
      "lastOpponentHumanOrAI"
    );
    generalMessageToSend.gameInfo.secondsBasedOnGameTimeType =
      localStorage.getItem("lastTimeLimit");
    generalMessageToSend.gameInfo.aiLevel = localStorage.getItem("lastAILevel");

    sendMessage(generalMessageToSend);
  };

  const playWithFriends = () => {
    const levelIndex = localStorage.getItem("lastSelectedIndexLevel");
    const opponentIndex = localStorage.getItem("lastSelectedOpponentIndex");
    const levelsObj = levels[levelIndex];
    const opponentObj = opponents[opponentIndex];

    if (!levelsObj || !opponentObj) {
      setOpenDialog(true);
      return;
    }

    navigate("/team-builder", { state: { userData } });
  };

  const handleSelectOpponentClick = () => setShowOpponentSelection(true);
  const handleSelectLevelClick = () => setShowLevelSelection(true);

  const onOpponentSelected = () => {
    setShowOpponentSelection(false);
    setButtonLabelsFromLocalStorage();

    checkBeforePlay();
  };

  function checkBeforePlay() {
    const levelId = localStorage.getItem("lastSelectedLevelId");
    const opponentIndex = localStorage.getItem("lastSelectedOpponentIndex");
    const storedOpponents = JSON.parse(localStorage.getItem("opponents"));

    const levelsObj = findLevelByTypeId(levelId);
    const opponentObj = storedOpponents?.[+opponentIndex];

    if (team !== null && opponentObj !== null) {
      let totalWhite = team.whiteTeam.length;
      let totalBlack = team.blackTeam.length;
      let players = opponentObj.players;

      if (totalWhite + totalBlack > players || totalWhite + totalBlack === 0) {
        Swal.fire({
          icon: "error",
          title: t(
            "Mismatch between the allowed number of players in the game and the number of players in the team"
          ),
          confirmButtonText: t("OK"),
        });
        return false;
      }
    }

    return true;
  }

  const onLevelSelected = () => {
    setShowLevelSelection(false);
    setButtonLabelsFromLocalStorage();
  };

  const onContinue = () => {
    setGameInfoDialogOpen(false);
    const uncompletedGameData = JSON.parse(
      localStorage.getItem("uncompleted_game")
    );
    if (uncompletedGameData) {
      const generalMessageToSend = { ...generalMessageTemplate };
      generalMessageToSend.messageType = "continue_game";
      generalMessageToSend.gameInfo = uncompletedGameData;

      sendMessage(generalMessageToSend);
    }
  };

  const getFontSize = () => (isMobile ? "12px" : "18px");
  const getButtonWidth = () => (isMobile ? "170px" : "230px");
  const getButtonHeight = () => (isMobile ? "55px" : "60px");

  const renderButton = (text, onClickHandler, image) => (
    <ChessButton
      onPress={onClickHandler}
      //primaryColor="#e0e0e0"
      //shadowColor="#424242"
      //hoverColor="#fafafa"
      text={t(text)}
      icon={
        image && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              marginRight: "10px",
            }}
          >
            {image}
          </span>
        )
      }
      fontColor="white"
      fontSize={getFontSize()}
      height={getButtonHeight()}
      width={getButtonWidth()}
    />
  );

  //console.log("Team ID received:", team?.id); // ✅ Check if it worked

  if (!team) {
    return <div>No team selected. Please go back and pick a team.</div>;
  }

  function goBack() {
    navigate(-1); // Navigates back to the previous page
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: isMobile ? 0 : 0,
        
        backgroundImage: `url("https://res.cloudinary.com/dnlgtek2a/image/upload/v1745759391/Pictures/team_bg1.png")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        height: "100vh",
    
      }}
    >
      {/* Use the NotEnoughCoinsDialog component */}
      <NotEnoughCoinsDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onBuyCoins={handleBuyCoins}
        onWatchAd={handleWatchAd}
        neededCoins={neededCoins}
        actualCoins={actualCoins}
      />
      {/* Title */}
      <Typography
        variant={isMobile ? "h6" : "h4"}
        align="center"
        mb={2}
        sx={{
          marginTop: isMobile ? "20px" : "50px",
          color: "white",
          //fontWeight: "bold",
          textTransform: "uppercase",
          //textShadow: "1px 1px 1px rgba(0, 0, 0, 0.3)",
        }}
      >
        <Profile
          open={profileOpen}
          onClose={handleCloseProfile}
          avatar={selectedAvatar}
        />
        {t("Team Play")}
      </Typography>
      <Divider sx={{ mb: 1, backgroundColor: "black" }} />

      {/* Team Name */}
      <Typography
        variant={isMobile ? "h6" : "h5"}
        align="center"
        color="primary.main"
        mb={3}
        sx={{
          color: "white",
          //textShadow: "1px 1px 1px rgba(0, 0, 0, 0.3)",
        }}
      >
        {team?.name || t("Unnamed Team")}
      </Typography>

      <div>
        <Stack
          direction="column"
          sx={{
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            zIndex: 200,
          }}
        >
          {/* Top Buttons */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              position: "absolute", // absolute positioning relative to parent
              marginTop: isMobile ? "220px" : "350px",
              //zIndex: 999, // make sure it's above the video
            }}
          >
            <Box textAlign="left">
              <Paper
                elevation={3}
                sx={{
                  backgroundColor: "#dfe6e9",
                  padding: isMobile ? 1 : 2,
                  borderRadius: 4,
                  border: "5px solid #4a707d",
                  height: "100%",
                  minWidth: isMobile ? "150px" : "250px", // 🔥 Optional extra: minimum size
                  maxWidth: isMobile ? "150px" : "250px",
                  maxHeight: isMobile ? "200px" : "320px",
                  minHeight: isMobile ? "200px" : "250px",
                }}
              >
                <Typography
                  variant={isMobile ? "h7" : "h6"}
                  align="center"
                  color="black"
                  mb={2}
                >
                  {t("White Team")}
                </Typography>
                <Divider sx={{ mb: 2, backgroundColor: "black" }} />
                {team.whiteTeam.map((player, idx) => (
                  <Paper
                  key={idx}
                  onClick={() => handleAvatarClick(player)}
                  sx={{
                    marginBottom: isMobile ? 0.5 : 1,
                    padding: isMobile ? 0.5 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: `3px solid white`,
                    borderRadius: 3,
                    backgroundColor: "white",
                    cursor: "pointer",
                    minHeight: isMobile ? 40 : 60,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#1976d2",
                      fontWeight: "bold",
                      fontSize: isMobile ? "0.7rem" : "1rem",
                    }}
                  >
                    {player.userStatistics.name}
                  </Typography>
                
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      src={player.url}
                      sx={{
                        width: isMobile ? 28 : 40,
                        height: isMobile ? 28 : 40,
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: player.online ? "green" : "red",
                        border: "2px solid white",
                      }}
                    />
                  </Box>
                </Paper>
                
                ))}
              </Paper>
            </Box>

            <Box textAlign="left">
              <Paper
                elevation={3}
                sx={{
                  backgroundColor: "black",
                  padding: isMobile ? 1 : 2,
                  borderRadius: 4,
                  border: "5px solid #4a707d",
                  height: "100%",
                  minWidth: isMobile ? "150px" : "250px", // 🔥 Optional extra: minimum size
                  maxWidth: isMobile ? "150px" : "250px",
                  maxHeight: isMobile ? "200px" : "320px",
                  minHeight: isMobile ? "200px" : "250px",
                }}
              >
                <Typography
                  variant={isMobile ? "h7" : "h6"}
                  align="center"
                  color="white"
                  mb={2}
                >
                  {t("Black Team")}
                </Typography>
                <Divider sx={{ mb: 2, backgroundColor: "white" }} />
                {team.blackTeam.map((player, idx) => (
                 <Paper
                 key={idx}
                 onClick={() => handleAvatarClick(player)}
                 sx={{
                   marginBottom: isMobile ? 0.5 : 1,
                   padding: isMobile ? 0.5 : 1,
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "space-between",
                   border: `3px solid white`,
                   borderRadius: 3,
                   backgroundColor: "white",
                   cursor: "pointer",
                   minHeight: isMobile ? 40 : 60,
                 }}
               >
                 <Typography
                   sx={{
                     color: "#1976d2",
                     fontWeight: "bold",
                     fontSize: isMobile ? "0.7rem" : "1rem",
                   }}
                 >
                   {player.userStatistics.name}
                 </Typography>
               
                 <Box sx={{ position: "relative" }}>
                   <Avatar
                     src={player.url}
                     sx={{
                       width: isMobile ? 28 : 40,
                       height: isMobile ? 28 : 40,
                     }}
                   />
                   <Box
                     sx={{
                       position: "absolute",
                       bottom: 0,
                       right: 0,
                       width: "10px",
                       height: "10px",
                       borderRadius: "50%",
                       backgroundColor: player.online ? "green" : "red",
                       border: "2px solid white",
                     }}
                   />
                 </Box>
               </Paper>
               
                ))}
              </Paper>
            </Box>
          </Stack>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginBottom: isMobile ? "40px" : "50px"
            }}
          >
            <ChessButton
              onPress={goBack}
              icon={<ArrowBackIcon />}
              text={t("Favorite_Back")}
              width={isMobile ? "120px" : "120px"}
              height={isMobile ? "50px" : "50px"}
              fontSize={isMobile ? "12px" : "12px"}
              primaryColor="#4a707d"
              hoverColor="#3b5a66"
              fontColor="white"
            />
          </Box>
        </Stack>
      </div>

      {/*Game Info Dialog */}
      <GameInfoDialog
        open={gameInfoDialogOpen}
        onClose={() => setGameInfoDialogOpen(false)}
        gameInfo={uncompletedGame}
        operations={true}
        onContinue={onContinue}
      />

      {/* Opponent and Level Selection Carousels */}
      {loading === false && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 200,
            width: isMobile ? "95%" : "auto", // responsive width
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {showOpponentSelection && (
            <OpponentSelectionCarousel
              onOpponentSelected={onOpponentSelected}
              onFriendSelected={playWithFriends}
              scaleConfig={{ selected: isMobile ? 1 : 1 }}
              setShowAppBar={setShowAppBar}
              team={team}
            />
          )}

          {showLevelSelection && (
            <LevelSelectionCarousel
              onLevelSelected={onLevelSelected}
              scaleConfig={{ selected: isMobile ? 1 : 1 }}
              setShowAppBar={setShowAppBar}
            />
          )}
        </Box>
      )}

      {!showOpponentSelection && !showLevelSelection && !loading && (
        <div>
          <Stack
            direction="column"
            sx={{
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              zIndex: 200,
            }}
          >
            {/* Top Buttons */}
            <Stack
              direction="row"
              spacing={3}
              sx={{
                position: "absolute", // absolute positioning relative to parent
                bottom: 130,
                zIndex: 999, // make sure it's above the video
              }}
            >
              <Box textAlign="center">
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "gold",
                    mb: "4px",
                    backgroundColor: "black",
                    px: "5px",
                    py: "2px",
                    borderRadius: "15px",
                    fontSize: { xs: "0.6rem", sm: "0.7rem" }, // smaller on mobile, normal on tablet+
                  }}
                >
                  {t("Select Your Game Type")}
                </Typography>
                {renderButton(
                  buttonSelectOpponent,
                  handleSelectOpponentClick,
                  <FortIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                )}
              </Box>

              <Box textAlign="center">
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "gold",
                    mb: "4px",
                    backgroundColor: "black",
                    px: "5px",
                    py: "2px",
                    borderRadius: "15px",
                    fontSize: { xs: "0.6rem", sm: "0.7rem" }, // responsive font size
                  }}
                >
                  {t("Select Outcome Rewards")}
                </Typography>
                {renderButton(
                  buttonSelectLevel,
                  handleSelectLevelClick,
                  <HowToVoteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                )}
              </Box>
            </Stack>
          </Stack>
        </div>
      )}

      {/* Play Button */}
      {loading === false && (
        <div
          style={{
            position: "absolute",
            marginRight: "auto",
            bottom: 35,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Play Button */}
          {showLevelSelection === false && showOpponentSelection === false && (
            <ChessButton
              onPress={playGame}
              icon={<PlayArrowIcon sx={{ fontSize: 20 }} />}
              text={t("Play!")}
              fontSize={getFontSize()}
              height={getButtonHeight()}
              width={getButtonWidth()}
            />
          )}
        </div>
      )}

      {/* Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            backgroundImage:
              "https://res.cloudinary.com/dnlgtek2a/image/upload/v1743415495/Pictures/cyume5jns2oovggj8z4g.png",
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: "15px",
            boxShadow: "10px 10px 20px rgba(0, 0, 0, 0.3)",
            opacity: 0.9,
            zIndex: -1,
          },
        }}
      >
        <DialogTitle
          sx={{
            marginTop: "50px",
            fontSize: "1.6rem",
            fontWeight: "bold",
            color: "black",
            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.3)",
          }}
        >
          {t("Selection Required")}
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "black",
            fontSize: "1.5rem",
            gap: "20px",
            padding: "10px",
          }}
        >
          <MovingText
            type="typewriter"
            dataText={[t("Select_opponent"), t("Level_Select")]}
          />

          <LottieAnimation
            path={
              "https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418439/Lottie/needToSelect.json"
            }
            width="100%"
            autoplay
            loop
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", width: "100%" }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="contained"
            sx={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              backgroundColor: "#ff5722",
              color: "white",
              padding: "10px 20px",
              borderRadius: "10px",
              boxShadow: "3px 3px 6px rgba(0, 0, 0, 0.2)",
              "&:hover": {
                backgroundColor: "#e64a19",
              },
            }}
          >
            {t("OK")}
          </Button>
        </DialogActions>
      </Dialog>

      <Stack
        direction="column"
        spacing={2}
        sx={{
          marginTop: isMobile ? "-30px" : "10px",
          zIndex: 200,
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {/* Loading Text Box */}
        {/* {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "black",
              color: "yellow",
              borderRadius: "10px",
              textAlign: "center",
              width: isMobile ? "90%" : "60%",
              padding: isMobile ? "10px" : "15px",
            }}
          >
            <Typography
              variant={isMobile ? "h6" : "h4"}
              sx={{ textAlign: "center", zIndex: 1000 }}
            >
              {t("Wait")}
            </Typography>
          </Box>
        )} */}

        {/* Loading Animation */}
        {loading && (
          <LottieAnimation
            path={
              "https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418492/Lottie/search.json"
            }
            width={isMobile ? "250px" : "400px"}
            height={isMobile ? "250px" : "400px"}
            autoplay
            loop
          />
        )}

        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "10px",
              zIndex: 2000, // ensure it's on top
              position: "relative",
            }}
          >
            <CountdownCircleTimer
              isPlaying
              duration={60}
              colors={["#4a707d", "#F7B801", "#A30000", "#A30000"]}
              colorsTime={[45, 30, 15, 0]}
              onComplete={() => ({ shouldRepeat: false, delay: 1 })}
            >
              {renderTime}
            </CountdownCircleTimer>
          </Box>
        )}

        {/* Stop Waiting Text Box */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "10px",
              backgroundColor: "transparent",
              textAlign: "center",
              width: isMobile ? "90%" : "60%",
              marginTop: isMobile ? "0px" : "50px",
            }}
          >
            {/* <Typography
              variant="h6"
              sx={{ textAlign: "center", color: "white", zIndex: 1000 }}
            >
              {t("Stop_Waiting")}
            </Typography> */}
            <ChessButton
              onPress={cancelGame}
              icon={<CancelIcon sx={{ fontSize: 20 }} />}
              text={t("Stop_Waiting")}
              fontSize="12px"
              primaryColor="red"
              height={getButtonHeight()}
              width={getButtonWidth()}
              borderRadius="10px"
            />
          </Box>
        )}

        {/* Cancel Animation */}
        {/* {loading && (
          <LottieAnimation
            path={"https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418553/Lottie/cancel.json"}
            width={isMobile ? "50px" : "60px"}
            height={isMobile ? "50px" : "60px"}
            marginTop={isMobile ? "-5px" : "20px"}
            autoplay
            loop={false}
            onClick={cancelGame}
          />
        )} */}
      </Stack>
    </div>
  );
};

export default TeamPlay;
